import type { DayOfWeek } from '@gozoki/api-types'

export type ReapproComputeAlgorithm = 'VMAX' | 'VMJ' | 'STOCK_PROJETE' | 'STOCK_MINIMUM'
export type ReaproPeriode = '3J' | '5J' | '7J'

export interface ReapproRealtimeIndicators {
  stockActuel: number
  stockDLCCourte: number
  stockArrivant: number
  vma3j: number
  vma5j: number
  vma7j: number
  vjm3j: number
  vjm5j: number
  vjm7j: number
}

export const EMPTY_REALTIME_INDICATORS: ReapproRealtimeIndicators = {
  stockActuel: 0,
  stockDLCCourte: 0,
  stockArrivant: 0,
  vma3j: 0,
  vma5j: 0,
  vma7j: 0,
  vjm3j: 0,
  vjm5j: 0,
  vjm7j: 0,
}

export const sanitizeNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const pickIndicatorByPeriode = (
  realtime: ReapproRealtimeIndicators | undefined,
  periode: ReaproPeriode,
): { vmaxBase: number; vmjBase: number } => {
  if (!realtime) {
    return { vmaxBase: 0, vmjBase: 0 }
  }
  if (periode === '3J') {
    return { vmaxBase: realtime.vma3j, vmjBase: realtime.vjm3j }
  }
  if (periode === '5J') {
    return { vmaxBase: realtime.vma5j, vmjBase: realtime.vjm5j }
  }
  return { vmaxBase: realtime.vma7j, vmjBase: realtime.vjm7j }
}

/**
 * Returns `initialCoef` if any of the covered days is a Saturday or Sunday,
 * otherwise `1` (the weekend coefficient does not apply when no weekend is covered).
 * @param coveringDays days of the week covered by the delivery
 * @param initialCoef the raw weekend coefficient to gate
 */
export const getReelWeekEndCoef = (
  coveringDays: readonly DayOfWeek[],
  initialCoef: number,
): number => {
  const isThereAWeekendInCoveringDays = coveringDays.some((day) =>
    ['saturday', 'sunday'].includes(day),
  )
  return isThereAWeekendInCoveringDays ? initialCoef : 1
}

export interface ComputeProductNeedInput {
  algorithm: ReapproComputeAlgorithm
  coverageRate: number
  coefSecu: number
  coefWeekend: number
  stockMinimum: number | null
  stockActuel: number
  stockDLCCourte: number
  stockArrivant: number
  vmaxBase: number
  vmjBase: number
  daysBefore?: number
  /** VMJ day-of-week weight for the production day (J). Defaults to 1. */
  poidsJourJ?: number
  /** VMJ day-of-week weight for the delivery day (J+1). Defaults to 1. */
  poidsJourJ1?: number
  /**
   * Sum of day-of-week weights for the EXTRA days this delivery must cover
   * BEYOND the delivery day itself (e.g. Sat + Sun for a Friday delivery
   * when there is no weekend dispatch). Multiplied by `coefSecu` in the VMJ
   * formula, on top of the existing `pJ1 × cs + tc × pJ` terms. Defaults
   * to 0 — formula then reduces to the legacy 2-day model.
   */
  extraCoverageWeights?: number
}

export interface ComputeProductNeedResult {
  besoin: number
  vmax: number
  vmj: number
  prevision: number
  stockProjete: number
  vmaxSubstitution: number
  vmjSubstitution: number
}

export const roundUpToPcb = (value: number, pcb: number): number => {
  if (pcb <= 0 || value <= 0) return value
  return Math.ceil(value / pcb) * pcb
}

export interface AllocationInput {
  storeId: string
  besoin: number // raw need (before PCB rounding)
  cappedNeed: number // need rounded up to nearest PCB multiple
  colisage: number
}

export const computeDispatchAllocations = (
  inputs: AllocationInput[],
  platformStock: number,
): Record<string, number> => {
  const totalCapped = inputs.reduce((sum, i) => sum + i.cappedNeed, 0)

  if (totalCapped <= 0 || platformStock <= 0) {
    return Object.fromEntries(inputs.map((i) => [i.storeId, 0]))
  }

  if (platformStock >= totalCapped) {
    return Object.fromEntries(inputs.map((i) => [i.storeId, i.cappedNeed]))
  }

  const totalBesoin = inputs.reduce((sum, i) => sum + i.besoin, 0)

  const isCase3 = platformStock <= totalBesoin
  const ratio = isCase3 ? platformStock / totalBesoin : platformStock / totalCapped

  const items = inputs.map((i) => {
    const base = isCase3 ? i.besoin : i.cappedNeed
    const rawAlloc = base * ratio
    const floored = Math.floor(rawAlloc / i.colisage) * i.colisage
    const remainder = rawAlloc % i.colisage
    return { storeId: i.storeId, floored, remainder, colisage: i.colisage }
  })

  const totalFloored = items.reduce((sum, i) => sum + i.floored, 0)
  let remainingStock = Math.round(platformStock - totalFloored)

  const sorted = [...items].sort((a, b) => b.remainder - a.remainder)
  const result: Record<string, number> = Object.fromEntries(
    items.map((i) => [i.storeId, i.floored]),
  )

  for (const item of sorted) {
    if (remainingStock < item.colisage) continue
    result[item.storeId] += item.colisage
    remainingStock -= item.colisage
  }

  return result
}

export const computeProductNeed = ({
  algorithm,
  coverageRate,
  coefSecu,
  coefWeekend,
  stockMinimum,
  stockActuel,
  stockDLCCourte,
  stockArrivant,
  vmaxBase,
  vmjBase,
  daysBefore = 0,
  poidsJourJ: rawPoidsJourJ,
  poidsJourJ1: rawPoidsJourJ1,
  extraCoverageWeights: rawExtraCoverageWeights,
}: ComputeProductNeedInput): ComputeProductNeedResult => {
  const sa = sanitizeNumber(stockActuel)
  const dlc = sanitizeNumber(stockDLCCourte)
  const arr = sanitizeNumber(stockArrivant)
  const db = Math.max(0, sanitizeNumber(daysBefore))
  const tc = Math.max(0, sanitizeNumber(coverageRate))
  const cs = Math.max(0, sanitizeNumber(coefSecu))
  const rawCw = Math.max(0, sanitizeNumber(coefWeekend))
  const cw = rawCw ?? 1
  const vmaxSignal = Math.max(0, sanitizeNumber(vmaxBase))
  const vmjSignal = Math.max(0, sanitizeNumber(vmjBase))
  const pJ = rawPoidsJourJ ?? 1
  const pJ1 = rawPoidsJourJ1 ?? 1
  const extra = Math.max(0, sanitizeNumber(rawExtraCoverageWeights ?? 0))
  const stockProjete = Math.max(0, Math.ceil(sa - Math.max(vmjSignal * db, dlc) + arr))
  const prevision = vmjSignal * tc
  const vmaxSubstitution = vmaxSignal * tc - sa + dlc - arr
  // VMJ formula (factored): vmj × ((pJ1 + extra) × cs + tc × pJ) − stockNet.
  // - `pJ1 × cs` covers the delivery day with safety (legacy).
  // - `tc × pJ` covers the residual at need-expression time, weighted by today (legacy).
  // - `extra × cs` adds weighted volume for any additional days this delivery must
  //   cover beyond the delivery day (e.g. Saturday + Sunday for a Friday delivery
  //   when there is no weekend dispatch). Defaults to 0 → legacy formula.
  const vmjSubstitution = vmjSignal * ((pJ1 + extra) * cs + tc * pJ) - (sa - dlc + arr)

  if (algorithm === 'STOCK_MINIMUM') {
    const customStockMin = sanitizeNumber(stockMinimum)
    const needFromCustomStockMin = customStockMin - (sa - dlc + arr)
    return {
      besoin: Math.max(0, Math.round(needFromCustomStockMin)),
      vmax: vmaxSignal,
      vmj: vmjSignal,
      prevision,
      stockProjete,
      vmaxSubstitution,
      vmjSubstitution,
    }
  }

  if (algorithm === 'STOCK_PROJETE') {
    return {
      besoin: Math.max(0, Math.round(vmaxSignal * cs * tc - stockProjete)),
      vmax: vmaxSignal,
      vmj: vmjSignal,
      prevision,
      stockProjete,
      vmaxSubstitution,
      vmjSubstitution,
    }
  }

  if (algorithm === 'VMAX') {
    return {
      besoin: Math.max(0, Math.round(vmaxSubstitution * cs * cw)),
      vmax: vmaxSignal,
      vmj: vmjSignal,
      prevision,
      stockProjete,
      vmaxSubstitution,
      vmjSubstitution,
    }
  }

  return {
    besoin: Math.max(0, Math.round(vmjSubstitution)),
    vmax: vmaxSignal,
    vmj: vmjSignal,
    prevision,
    stockProjete,
    vmaxSubstitution,
    vmjSubstitution,
  }
}

// Round a need up to the nearest multiple of colisage (units per case).
// Returns the need unchanged if colisage <= 1 or need <= 0.
export const roundUpToColisage = (need: number, colisage: number): number => {
  if (colisage <= 1 || need <= 0) return need
  return Math.ceil(need / colisage) * colisage
}

/**
 * Applique un multiplicateur de besoin daté (ajustement ponctuel par date de
 * livraison) à un besoin entier. Borné à 0, arrondi au plus proche. `multiplier=1`
 * = no-op ; `multiplier=0` annule la repro de cette livraison.
 */
export const applyNeedAdjustment = (need: number, multiplier: number): number =>
  Math.max(0, Math.round(need * sanitizeNumber(multiplier)))

export interface FinalizeReapproNeedOptions {
  stopOrdering: boolean
  colisage: number
  needAdjustmentMultiplier?: number
}

export const finalizeReapproNeed = (
  rawNeed: number,
  { stopOrdering, colisage, needAdjustmentMultiplier = 1 }: FinalizeReapproNeedOptions,
): number => {
  const adjustedNeed = stopOrdering ? 0 : applyNeedAdjustment(rawNeed, needAdjustmentMultiplier)
  return roundUpToColisage(adjustedNeed, colisage)
}

export interface ComputeReapproBesoinInput extends ComputeProductNeedInput {
  /** Jours de la semaine couverts par cette livraison (calendrier de production). Sert au gating week-end. */
  coveringDays: readonly DayOfWeek[]
  /** Commande arrêtée pour ce produit → besoin forcé à 0. */
  stopOrdering: boolean
  /** Unités par colis ; le besoin est arrondi au multiple supérieur. */
  colisage: number
  /**
   * Multiplicateur de besoin daté (ajustement ponctuel par date de livraison).
   * Défaut 1 = no-op. Appliqué au rawNeed AVANT l'arrondi colisage, pour garder
   * des quantités commandables en multiples de colisage.
   */
  needAdjustmentMultiplier?: number
}

export interface ComputeReapproBesoinResult {
  besoin: number
  rawNeed: number
  usedCoefWeekend: number
}

/**
 * Source de vérité UNIQUE du besoin de réappro, partagée serveur (preview + cron)
 * et client (simulateur BO). Compose les helpers existants exactement comme le
 * faisait `computeProductReapproLine` côté serveur, pour une parité par construction.
 */
export const computeReapproBesoin = (
  input: ComputeReapproBesoinInput,
): ComputeReapproBesoinResult => {
  const usedCoefWeekend = getReelWeekEndCoef(input.coveringDays, input.coefWeekend)
  const rawNeed = computeProductNeed({ ...input, coefWeekend: usedCoefWeekend }).besoin
  const besoin = finalizeReapproNeed(rawNeed, input)
  return { besoin, rawNeed, usedCoefWeekend }
}

export interface ComputeForecastReapproBesoinInput {
  coveredDemand: number
  intermediateDemand: number
  coefSecu: number
  stockActuel: number
  stockDLCCourte: number
  stockArrivant: number
  stopOrdering: boolean
  colisage: number
  needAdjustmentMultiplier?: number
}

export interface ComputeForecastReapproBesoinResult {
  besoin: number
  rawNeed: number
  projectedStock: number
}

export const computeForecastReapproBesoin = (
  input: ComputeForecastReapproBesoinInput,
): ComputeForecastReapproBesoinResult => {
  const projectedStock = Math.max(
    0,
    input.stockActuel - input.stockDLCCourte + input.stockArrivant - input.intermediateDemand,
  )
  const rawNeed = Math.max(0, Math.round(input.coveredDemand * input.coefSecu - projectedStock))
  const besoin = finalizeReapproNeed(rawNeed, input)
  return { besoin, rawNeed, projectedStock }
}

export interface GroupCapLine {
  storeId: string
  productId: string
  besoin: number
  colisage: number
}

export const buildGroupCapKey = (storeId: string, productId: string) => `${storeId}|${productId}`

export const applyGroupProductionCap = (
  lines: GroupCapLine[],
  maxProduction: number,
): Map<string, number> => {
  if (maxProduction <= 0) {
    return new Map(lines.map((l) => [buildGroupCapKey(l.storeId, l.productId), 0]))
  }

  const totalBesoin = lines.reduce((sum, l) => sum + l.besoin, 0)
  if (totalBesoin <= maxProduction) {
    return new Map(lines.map((l) => [buildGroupCapKey(l.storeId, l.productId), l.besoin]))
  }

  const besoinByStore = new Map<string, number>()
  for (const l of lines) {
    besoinByStore.set(l.storeId, (besoinByStore.get(l.storeId) ?? 0) + l.besoin)
  }

  const storeQuotas = computeDispatchAllocations(
    [...besoinByStore.entries()].map(([storeId, besoin]) => ({
      storeId,
      besoin,
      cappedNeed: besoin,
      colisage: 1,
    })),
    maxProduction,
  )

  const linesByStore = new Map<string, GroupCapLine[]>()
  for (const l of lines) {
    const list = linesByStore.get(l.storeId) ?? []
    list.push(l)
    linesByStore.set(l.storeId, list)
  }

  const result = new Map<string, number>()
  for (const [storeId, storeLines] of linesByStore) {
    const quota = storeQuotas[storeId] ?? 0
    const allocations = computeDispatchAllocations(
      storeLines.map((l) => ({
        storeId: l.productId,
        besoin: l.besoin,
        cappedNeed: roundUpToColisage(l.besoin, Math.max(1, l.colisage)),
        colisage: Math.max(1, l.colisage),
      })),
      quota,
    )
    for (const l of storeLines) {
      result.set(buildGroupCapKey(l.storeId, l.productId), allocations[l.productId] ?? 0)
    }
  }

  return result
}

export interface ProductCapLine {
  storeId: string
  /**
   * Besoin de cette boutique pour le produit, **déjà arrondi au colisage** en
   * amont par `computeReapproBesoin`. Le chemin passthrough (besoin total sous
   * le cap) le renvoie tel quel, sans ré-arrondir : passer un besoin non
   * arrondi produirait donc des quantités non commandables, silencieusement.
   */
  besoin: number
  colisage: number
}

/**
 * Plafond de production au niveau d'UN produit, réparti sur les boutiques du
 * réseau au prorata du besoin, en colis entiers.
 *
 * Contrairement à `applyGroupProductionCap` (N produits × M boutiques, donc
 * prorata à deux étages), un cap produit ne croise qu'un produit : un seul
 * étage de répartition suffit.
 *
 * Hypothèses d'appel, non vérifiées à l'exécution :
 * - **une seule ligne par boutique** — un `storeId` dupliqué serait écrasé
 *   silencieusement (et pourrait être crédité deux fois au tour de reliquat) ;
 * - **`besoin` déjà arrondi au colisage** (cf. `ProductCapLine.besoin`), sans
 *   quoi l'invariant « allocation multiple du colisage » ne tient que sur le
 *   chemin cap, pas sur le passthrough.
 */
export const applyProductProductionCap = (
  lines: ProductCapLine[],
  maxProduction: number,
): Map<string, number> => {
  if (maxProduction <= 0) {
    return new Map(lines.map((l) => [l.storeId, 0]))
  }

  const totalBesoin = lines.reduce((sum, l) => sum + l.besoin, 0)
  if (totalBesoin <= maxProduction) {
    return new Map(lines.map((l) => [l.storeId, l.besoin]))
  }

  const allocations = computeDispatchAllocations(
    lines.map((l) => ({
      storeId: l.storeId,
      besoin: l.besoin,
      cappedNeed: roundUpToColisage(l.besoin, Math.max(1, l.colisage)),
      colisage: Math.max(1, l.colisage),
    })),
    maxProduction,
  )

  return new Map(lines.map((l) => [l.storeId, allocations[l.storeId] ?? 0]))
}

/**
 * Écrête un besoin par un facteur de plafonnement, en restant sur un multiple du
 * colisage. `factor` à `null` signifie « le plafond ne mord pas » → besoin inchangé.
 * Arrondi vers le BAS : on ne livre jamais au-dessus du plafond.
 *
 * Contrairement à `applyProductProductionCap`, cette primitive ne voit qu'UNE
 * ligne : elle ne redistribue aucun reliquat, et peut rendre jusqu'à un colis
 * de moins que le prorata réel (0 là où l'allocation donne un colis). Réservée
 * aux recalculs LOCAUX du simulateur BO, qui ne connaît pas le réseau ; au
 * repos, le serveur sert l'allocation du prorata réel, jamais ce facteur.
 *
 * `factor` vaut `max / total` : `besoin × factor` est un rationnel dont le
 * flottant tombe parfois juste SOUS un entier (55 × 7/55 = 6,999999999999999)
 * et un `floor` nu perdrait alors un colis entier. L'epsilon absorbe cette
 * erreur (≈ valeur × 4e-16) en restant très en dessous du plus petit écart réel
 * sous un entier, 1 / (total × colisage) : il ne crée jamais un colis au-dessus
 * du plafond (balayage exhaustif : cf. commit, zéro arrondi vers le haut).
 */
export const applyCapFactor = (besoin: number, factor: number | null, colisage: number): number => {
  if (factor === null) return besoin
  const pcb = Math.max(1, colisage)
  return Math.floor((besoin * factor) / pcb + 1e-9) * pcb
}
